import { Modal } from 'acma-spa';
import { useBrowserPrompt } from 'acma-spa/dist/blockers/BrowserPrompt';
import { useCommitedState } from 'acma-spa/dist/blockers/CommitedState';
import { RouterPromptModal } from 'acma-spa/dist/blockers/RouterPromptModal';
import { ButtonDropdownItem } from 'acma-spa/dist/buttons/ButtonDropdown';
import { FileDownloadFormats, FormContext } from 'acma-spa/dist/forms/Types';
import { AlertProvider } from 'acma-spa/dist/layout/Alert';
import { Spinner } from 'acma-spa/dist/layout/Spinner';
import React, { useState, ReactNode, useEffect } from 'react';

export interface ModalFormProps<M> {
  id: string;
  title: string;
  open: boolean;
  closeDlg: () => any;
  onConfirm: (m: M) => Promise<M>;
  onLoad: () => Promise<[M, number]>;
  onSubmit: (m: M) => Promise<M>;
  onExit: (m: M, submitted: boolean) => number | null | undefined;
  onPrint?: (m: M, format: FileDownloadFormats) => Promise<any>;
  onSave?: (m: M) => Promise<M>;
  children?: (ctx: FormContext<M> | null) => ReactNode;
  readOnly?: boolean;
  additionalDownloadButtons?: ButtonDropdownItem[];
}

export const ModalForm = <M extends {}>(props: ModalFormProps<M>) => {
  const [model, setModel] = useState<M | null>(null);
  const [loadingText, setLoadingText] = useState<string | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);

  const load = async () => {
    setErrorText(null);
    setLoadingText('Loading...');
    try {
      const [m, p] = await props.onLoad();
      onChange(m, true);
      setPage(p);
    } catch (e: any) {
      setErrorText(e.message ? e.message : 'Unexpected error');
    } finally {
      setLoadingText(null);
    }
    return null;
  };

  useEffect(() => {
    load();
  }, []);

  // Configure state and commited state
  const [commit, isUncommited] = useCommitedState(model);
  const onChange = (model: M, isCommit?: boolean) => {
    setModel(model);
    if (isCommit) {
      commit();
    }
  };

  // Configure browser blocker
  useBrowserPrompt(isUncommited);

  // Setup page navigation
  const [page, setPage] = useState<number>(0);

  // Print data
  const print = async (d: M, format: FileDownloadFormats) => {
    try {
      setLoadingText('Downloading...');
      if (props.onPrint != null) {
        await props.onPrint(d, format);
      }
    } catch (e: any) {
      setErrorText(e.message ? e.message : 'Unexpected error');
    } finally {
      setLoadingText(null);
    }
  };

  const navigate = async (d: M, newPage?: number | boolean, submit?: boolean) => {
    setErrorText(null);
    setLoadingText('Saving...');
    try {
      if (!props.readOnly) {
        if (submit) {
          d = await props.onSubmit(d);
        } else if (props.onSave != null) {
          d = await props.onSave(d);
        }
        onChange(d, true);
      }
      props.onExit(d, submit ?? false);

      return d;
    } catch (e: any) {
      setErrorText(e.message ? e.message : 'Unexpected error');
    } finally {
      setLoadingText(null);
    }
    return null;
  };

  const ctx: FormContext<M> | null = model
    ? {
        model: model,
        additionalDownloadButtons: props.additionalDownloadButtons,
        readOnly: props.readOnly ? true : false,
        onSaveEnabled: props.onSave ? true : false,
        onNavigate: navigate,
        page: 0,
        getNextPage: () => 0,
        getPreviousPage: () => 0,
        onChange: onChange,
        onError: setErrorText,
        onLoading: setLoadingText,
        onPrint: props.onPrint ? print : undefined,
      }
    : null;

  // Render standard elements and current page
  return (
    <>
      <Modal id={props.id} title={props.title} open={props.open} onClose={props.closeDlg} size={'xl'} backdrop={'static'} keyboard={false}>
        <Spinner isLoading={loadingText ? true : false} loadingText={loadingText || undefined}>
          {errorText ? <AlertProvider messages={[errorText]} dismiss={true} color="danger" /> : <></>}
          <div id="form-container">{props.children && props.children(ctx)}</div>
        </Spinner>
        <RouterPromptModal block={isUncommited} />
      </Modal>
    </>
  );
};
